/**
 * ys142857 subAccount page — bottom 一键归集 modal (pay_pwd + confirm).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { THEMED_SURFACE_COLORS as C, ThemedNotice, ThemedSubmitButton } from '../ui/ThemedSurface';
import { TransactionSecurity } from '../ui/TransactionSecurity';

export type ImputationTotals = {
  ep?: string;
  rp?: string;
  sp?: string;
  mp?: string;
  fp?: string;
  fc?: string;
};

export function SubAccountImputationModal({
  open,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (payload: { transaction_password: string; security_answer: string }) => void;
}) {
  const { t } = useTranslation();
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);

  if (!open) return null;

  const canSubmit = txPassword.trim().length > 0 && securityAnswer.trim().length > 0 && !pending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-5">
      <button type="button" className="absolute inset-0 bg-black/70" aria-label={t('common.cancel')} onClick={onClose} />
      <div
        className="relative w-full max-w-[340px] overflow-hidden rounded-2xl border"
        style={{ borderColor: C.line, background: '#131F2F' }}
      >
        <div className="px-4 py-3 text-center text-sm font-semibold" style={{ color: C.text, background: '#222D3F' }}>
          {t('subAccounts.imputationTitle', '一键归集')}
        </div>
        <div className="px-4 py-4">
          <p className="mb-4 text-center text-xs leading-relaxed" style={{ color: C.muted }}>
            {t('subAccounts.imputationHint', '将所有子账户 EP/RP/SP/MP/FP/FC 汇集到主账户，操作不可撤回。')}
          </p>
          <TransactionSecurity
            txPassword={txPassword}
            onTxPasswordChange={setTxPassword}
            showSecurity={showSecurity}
            onShowSecurityChange={setShowSecurity}
            securityAnswer={securityAnswer}
            onSecurityAnswerChange={setSecurityAnswer}
            disabled={pending}
            colors={C}
          />
          {error ? (
            <div className="mt-3">
              <ThemedNotice tone="error">{error}</ThemedNotice>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3 px-4 pb-4">
          <button
            type="button"
            disabled={pending}
            onClick={onClose}
            className="rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
            style={{ background: '#4986FB', color: '#FFF' }}
          >
            {t('common.cancel')}
          </button>
          <ThemedSubmitButton
            disabled={!canSubmit}
            onClick={() => onConfirm({ transaction_password: txPassword, security_answer: securityAnswer })}
            className="!py-3"
          >
            {pending ? '...' : t('common.confirm')}
          </ThemedSubmitButton>
        </div>
      </div>
    </div>
  );
}

export function formatImputationTotals(totals: ImputationTotals): string {
  const parts: string[] = [];
  if (totals.ep && Number(totals.ep) > 0) parts.push(`EP:${trimNum(totals.ep)}`);
  if (totals.rp && Number(totals.rp) > 0) parts.push(`RP:${trimNum(totals.rp)}`);
  if (totals.sp && Number(totals.sp) > 0) parts.push(`SP:${trimNum(totals.sp)}`);
  if (totals.mp && Number(totals.mp) > 0) parts.push(`MP:${trimNum(totals.mp)}`);
  if (totals.fp && Number(totals.fp) > 0) parts.push(`FP:${trimNum(totals.fp)}`);
  if (totals.fc && Number(totals.fc) > 0) parts.push(`FC:${trimNum(totals.fc)}`);
  return parts.join(',');
}

function trimNum(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
